import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { companyInfo } from '../mockData';
import { useCountUp, useInView } from '../hooks/useCountUp';

const Hero = () => {
  const [statsRef, isStatsInView] = useInView();
  const [scrollY, setScrollY] = useState(0);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [animationDone, setAnimationDone] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const experienceCount = useCountUp(companyInfo.stats.experience, 4500, isStatsInView);
  const projectsCount = useCountUp(companyInfo.stats.projects, 4800, isStatsInView);
  const qualityCount = useCountUp(companyInfo.stats.quality, 5000, isStatsInView);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Initial load animation
  useEffect(() => {
    const timer = setTimeout(() => {
      setHasLoaded(true);
    }, 500);
    
    // After entrance animation completes (~5s), enable scroll animation
    const animTimer = setTimeout(() => {
      setAnimationDone(true);
    }, 5000);
    
    return () => {
      clearTimeout(timer);
      clearTimeout(animTimer);
    };
  }, []);

  // Parallax effect with aggressive throttle for mobile
  useEffect(() => {
    let ticking = false;
    let lastScrollY = 0;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // On mobile - update less frequently (every 20px)
      if (isMobile && Math.abs(currentScrollY - lastScrollY) < 20) {
        return;
      }

      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrollY(currentScrollY);
          lastScrollY = currentScrollY;
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMobile]);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      const headerOffset = isMobile ? 100 : 150; // Larger offset for desktop
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Calculate text position - subtle animation on mobile, full on desktop
  const getTextTransform = (direction) => {
    if (!hasLoaded) {
      return direction === 'left' ? '-100%' : '100%';
    }

    // Mobile - subtle scroll animation with very small spread
    if (isMobile) {
      const maxSpread = 15; // Very small movement
      const speed = 0.08; // Very slow
      const spread = Math.min(scrollY * speed, maxSpread);
      return direction === 'left' ? `-${spread}%` : `${spread}%`;
    }

    // Desktop - full animation
    const spread = Math.min(scrollY * 0.25, 80);
    return direction === 'left' ? `-${spread}%` : `${spread}%`;
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden">

      {/* Background Image with Parallax */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_493e352d-df2b-42b3-aab8-838c7ce910cf/artifacts/pfbibq5b_%D0%9A%D0%B0%D1%81%D0%BA%D0%B8%20%D0%B7%20%D0%BB%D0%BE%D0%B3%D0%BE%D1%82%D0%B8%D0%BF%D0%BE%D0%BC%20NSL.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
            backgroundRepeat: 'no-repeat',
            minHeight: '120vh',
            // Disable parallax on mobile for better performance
            transform: isMobile ? 'none' : `translate3d(0, ${scrollY * 0.5}px, 0) scale(1.1)`,
            willChange: isMobile ? 'auto' : 'transform'
          }} />

        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/80" />
      </div>

      {/* Content with Parallax */}
      <div
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 md:py-40 overflow-x-hidden"
        style={{
          transform: isMobile ? 'none' : `translate3d(0, ${scrollY * 0.3}px, 0)`,
          opacity: Math.max(1 - scrollY / 1000, 0),
          filter: isMobile ? `blur(${Math.min(scrollY / 150, 8)}px)` : 'none',
          willChange: isMobile ? 'auto' : 'transform, opacity'
        }}>

        <div className="max-w-4xl overflow-x-hidden">
          {/* Main Heading - Slow smooth entrance animation via CSS */}
          <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-white mb-6 leading-tight lg:leading-[0.98] tracking-tight">
            <span
              className={`block !ml-[18px] !mr-[10px] ${!animationDone ? 'hero-word-1' : ''}`}
              style={{
                transform: animationDone ? `translate3d(${getTextTransform('left')}, 0, 0)` : undefined,
                transition: animationDone ? (isMobile ? 'transform 0.15s linear' : 'transform 0.3s ease-out') : undefined
              }}>
              Gradimo
            </span>
            <span
              className={`block !ml-[18px] !mr-[10px] ${!animationDone ? 'hero-word-2' : ''}`}
              style={{
                transform: animationDone ? `translate3d(${getTextTransform('right')}, 0, 0)` : undefined,
                transition: animationDone ? (isMobile ? 'transform 0.15s linear' : 'transform 0.3s ease-out') : undefined
              }}>
              Vašu
            </span>
            <span
              className={`block !ml-[18px] !mr-[10px] text-[#cab28e] ${!animationDone ? 'hero-word-3' : ''}`}
              style={{
                transform: animationDone ? `translate3d(${getTextTransform('left')}, 0, 0)` : undefined,
                transition: animationDone ? (isMobile ? 'transform 0.15s linear' : 'transform 0.3s ease-out') : undefined
              }}>
              Budućnost
            </span>
            <span
              className={`block !ml-[18px] !mr-[10px] ${!animationDone ? 'hero-word-4' : ''}`}
              style={{
                transform: animationDone ? `translate3d(${getTextTransform('right')}, 0, 0)` : undefined,
                transition: animationDone ? (isMobile ? 'transform 0.15s linear' : 'transform 0.3s ease-out') : undefined
              }}>
              Na Čvrstim
            </span>
            <span
              className={`block !ml-[18px] !mr-[10px] text-[#cab28e] ${!animationDone ? 'hero-word-5' : ''}`}
              style={{
                transform: animationDone ? `translate3d(${getTextTransform('left')}, 0, 0)` : undefined,
                transition: animationDone ? (isMobile ? 'transform 0.15s linear' : 'transform 0.3s ease-out') : undefined
              }}>
              Temeljima
            </span>
          </h1>

          {/* Description */}
          <p className="md:text-xl max-w-2xl !text-lg !mb-[32px] !ml-[11px] text-gray-300">
            {companyInfo.description}
          </p>

          {/* CTA Button */}
          <Button
            onClick={() => scrollToSection('#projects')}
            className="inline-flex items-center justify-center whitespace-nowrap focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 h-9 hover:bg-[#b8a07d] transition-all duration-300 hover:scale-105 !font-semibold !text-base !ml-[15px] !mr-[15px] !pt-[24px] !pb-[24px] !pl-[10px] !pr-[10px] !gap-[8px] rounded-md shadow text-black bg-[#cab28e]">

            POGLEDAJTE NAŠE PROJEKTE
          </Button>

          {/* Stats with Animation */}
          <div 
            ref={statsRef} 
            className="grid grid-cols-3 gap-6 md:gap-16 mt-12 md:mt-8 pt-12 md:pt-8 border-t border-gray-700/50 md:border-[#cab28e]/20"
            style={{
              filter: isMobile ? 'inherit' : 'none'
            }}
          >
            <div className="transform transition-all duration-500 hover:scale-105">
              <div className="text-4xl md:text-5xl font-bold text-[#cab28e] mb-2 tabular-nums">
                {experienceCount}
              </div>
              <div className="text-gray-400 text-sm md:text-sm tracking-wide md:uppercase md:tracking-[0.14em]">
                {companyInfo.stats.experienceLabel}
              </div>
            </div>
            <div className="transform transition-all duration-500 hover:scale-105">
              <div className="text-4xl md:text-5xl font-bold text-[#cab28e] mb-2 tabular-nums">
                {projectsCount}
              </div>
              <div className="text-gray-400 text-sm md:text-sm tracking-wide md:uppercase md:tracking-[0.14em]">
                {companyInfo.stats.projectsLabel}
              </div>
            </div>
            <div className="transform transition-all duration-500 hover:scale-105">
              <div className="text-4xl md:text-5xl font-bold text-[#cab28e] mb-2 tabular-nums">
                {qualityCount}
              </div>
              <div className="text-gray-400 text-sm md:text-sm tracking-wide md:uppercase md:tracking-[0.14em]">
                {companyInfo.stats.qualityLabel}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10"
        style={{
          opacity: Math.max(1 - scrollY / 300, 0)
        }}>

        <div className="animate-bounce">
          <div className="w-6 h-10 border-2 border-[#cab28e] rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-[#cab28e] rounded-full" />
          </div>
        </div>
      </div>
    </section>);

};

export default Hero;